import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
export const _frontmatter = {
  "title": "Onshore Renewables | How revenue works",
  "description": "The Outer Continental Shelf Lands Act (OCSLA) of 1953 grants the Secretary of the Department of the Interior (DOI) the authority to manage offshore energy resources and to develop regulations to carry out that authority. Three agencies within DOI — BOEM, BSEE, and ONRR — work together to manage, regulate, and collect revenue from leased acres across the Outer Continental Shelf.",
  "includeToc": false,
  "tags": ["How revenue works", "Onshore", "Oil", "Gas", "Process", "Resources"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageTabs = makeShortcode("PageTabs");
const PageTab = makeShortcode("PageTab");
const Box = makeShortcode("Box");
const Container = makeShortcode("Container");
const Grid = makeShortcode("Grid");
const BlmLogoImg = makeShortcode("BlmLogoImg");
const OnshoreRenewablesBackground = makeShortcode("OnshoreRenewablesBackground");
const DidYouKnowCard = makeShortcode("DidYouKnowCard");
const CollapsibleContent = makeShortcode("CollapsibleContent");
const ProcessCard = makeShortcode("ProcessCard");
const GlossaryTerm = makeShortcode("GlossaryTerm");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageTabs mdxType="PageTabs">
  <PageTab alt={'Navigate to Offshore Renewables'} to={'/how-revenue-works/offshore-renewables/'} mdxType="PageTab">
    Offshore
  </PageTab>
  <PageTab mdxType="PageTab">
    Onshore
  </PageTab>
    </PageTabs>
    <Box bgcolor='primary.main' mdxType="Box">
      <Container maxWidth="lg" mdxType="Container">
        <Grid container alignItems='center' mdxType="Grid">
          <Grid item xs={12} sm={8} mdxType="Grid">
            <Box p={3} pl={0} mdxType="Box">
              <p><a parentName="p" {...{
                  "href": "/sites/whats-new-v7.10.0/how-revenue-works/"
                }}>{`How revenue works /`}</a></p>

              <h1 {...{
                "id": "Onshore-Renewables",
                "style": {
                  "position": "relative"
                }
              }}>{`Onshore Renewables`}</h1>
              <p>{`Onshore renewable energy development on federal lands includes solar, wind, and geothermal energy. Title V of the Federal Land Policy and Management Act of 1976 provides BLM with the authority to issue rights of way for developing renewable energy projects on federal lands.`}</p>
            </Box>
          </Grid>
          <Grid container xs={12} sm={4} mdxType="Grid">
            <Box p={3} border={4} borderTop={0} borderBottom={0} borderColor='white' mdxType="Box">
              <Grid container xs={12} mdxType="Grid">
                <Grid item xs={8} mdxType="Grid">
                  <p>{`Onshore renewables are managed and regulated by the `}<a parentName="p" {...{
                      "href": "https://www.blm.gov/"
                    }}>{`Bureau of Land Management `}<OpenInNewIcon style={{
                        fontSize: 20
                      }} mdxType="OpenInNewIcon" /></a>{` (BLM).`}</p>
                </Grid>
                <Grid item xs={4} mdxType="Grid">
                  <Box p={4} mdxType="Box"><BlmLogoImg mdxType="BlmLogoImg"></BlmLogoImg></Box>
                </Grid>
              </Grid>
              <hr />
              <p><a parentName="p" {...{
                  "href": "../../?tab=tab-fact-sheets"
                }}>{`Renewables fact sheets`}</a></p>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
    <OnshoreRenewablesBackground mdxType="OnshoreRenewablesBackground">
      <Grid container spacing={2} alignItems='flex-start' mdxType="Grid">
        <Grid item xs={12} sm={4} mdxType="Grid">
          <Box p={9} m={9} display={{
            xs: 'none',
            sm: 'block'
          }} mdxType="Box" />
          <Box p={8} display={{
            xs: 'block',
            sm: 'none'
          }} mdxType="Box" />
          <DidYouKnowCard mdxType="DidYouKnowCard">
            <p>{`BLM tracks the status of solar, wind, and geothermal projects on federal land.`}</p>
            <CollapsibleContent mdxType="CollapsibleContent">
              <p>{`You can review `}<a parentName="p" {...{
                  "href": "https://www.blm.gov/programs/energy-and-minerals/renewable-energy"
                }}>{`current and historical renewable energy data on BLM's website `}<OpenInNewIcon style={{
                    fontSize: 20
                  }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
            </CollapsibleContent>
          </DidYouKnowCard>
        </Grid>
        <Grid item xs={12} sm={1} mdxType="Grid">
        </Grid>
        <Grid item xs={12} sm={7} mdxType="Grid">
          <Box mt={5} mdxType="Box">
            <ProcessCard step={1} name={'Plan'} mdxType="ProcessCard">
              <p>{`Over the past decade, the federal government has taken steps to update regulations for renewable energy. These updates clarify and encourage planning for renewable energy development on federal lands, particularly for wind and solar energy.`}</p>
              <ul>
                <li parentName="ul">
                  <p parentName="li">{`In 2005, BLM published an `}<GlossaryTerm mdxType="GlossaryTerm">{`Environmental Impact Statement (EIS)`}</GlossaryTerm>{` to create a framework for developing wind energy on federal lands in the West. At the same time, BLM amended 53 land-use plans to allow for wind energy development. BLM field offices can amend additional land-use plans any time to support wind energy development.`}</p>
                </li>
                <li parentName="ul">
                  <p parentName="li">{`In 2012, BLM released the final solar EIS. This statement furthered a proposal to create a competitive leasing program on federal lands for commercial scale solar development in 17 new `}<a parentName="p" {...{
                      "href": "https://www.blm.gov/programs/energy-and-minerals/renewable-energy/solar-energy/permitting-and-development/land-use-planning"
                    }}>{`Solar Energy Zones `}<OpenInNewIcon style={{
                        fontSize: 20
                      }} mdxType="OpenInNewIcon" /></a>{`.`}</p>
                </li>
              </ul>
              <p>{`BLM finalized `}<a parentName="p" {...{
                  "href": "https://www.blm.gov/sites/blm.gov/files/Energyandminerals_Renewable_Wind_solar_finalrule.pdf"
                }}>{`a new rule (PDF) `}<OpenInNewIcon style={{
                    fontSize: 20
                  }} mdxType="OpenInNewIcon" /></a>{` that promotes competitively offering designated solar and wind energy development leasing areas. Designated leasing areas have fewer environmental and cultural resource conflicts than others. Fewer conflicts make these areas better candidates for development.`}</p>
            </ProcessCard>
            <ProcessCard step={2} name={'Lease'} mdxType="ProcessCard">
              <p>{`BLM regulations allow for two distinct processes for awarding rights to apply to develop wind and solar energy projects on federal lands: one within designated leasing areas, and one outside of designated leasing areas. `}</p>
              <br />
              <p>{`Within designated leasing areas, BLM competitively offers leases for parcels of land for wind and solar energy development. Interested parties offer bids for the lease. BLM awards the lease to the highest bidder, so long as the payment is greater than or equal to the minimum bid (based on administrative costs incurred by the government and the estimated value of the land) and 20% of the bonus bid (any amount the bidder is willing to pay beyond the minimum bid). Some bidders may pre-qualify for offsets up to 20% of the high bid.`}</p>
              <br />
              <p>{`Outside of designated leasing areas, BLM offers right-of-way grants through a competitive process. BLM holds pre-application meetings and screens all applicants. Where there are two or more applicants (or at BLM’s discretion), BLM offers right-of-way grants by holding a competitive offer. `}</p>
              <br />
              <p>{`The highest bidder becomes the preferred applicant. Then the highest bidder is the only party that can apply for a right-of-way grant for a given parcel of land. BLM offers the applicant a grant at its discretion. There would be no bid offsets outside of the designated leasing areas. Subsequent site-specific National Environmental Policy Act (NEPA) analysis is required for right-of-way grants.`}</p>
            </ProcessCard>
            <ProcessCard step={3} name={'Explore'} mdxType="ProcessCard">
              <p>{`Companies explore leased land to locate suitable sites for constructing renewable infrastructure. BLM grants a developer a lease or right of way. Once granted, the developer has a period to construct facilities and begin generating electricity from wind or solar energy.`}</p>
              <br />
Exploration for onshore renewables is easier and faster than for offshore renewables. Within designated leasing areas, a developer has two years to find a suitable site for infrastructure and submit a Plan of Development. Outside of designated leasing areas, a developer has two years to begin construction. Companies can choose whether to extend their leases based on their findings. During exploration, companies pay <GlossaryTerm mdxType="GlossaryTerm">rent</GlossaryTerm> to BLM.
            </ProcessCard>
            <ProcessCard step={4} name={'Develop'} mdxType="ProcessCard">
              <p>{`Once a developer submits a Plan of Development, they have a period to construct facilities and begin generating electricity from wind or solar energy. Within designated leasing areas, a developer has seven years to complete construction. Outside of designated leasing areas, a developer has two years. During this phase, the developer pays `}<GlossaryTerm termKey="Megawatt Capacity (MC) fee" mdxType="GlossaryTerm">{`Megawatt Capacity fees`}</GlossaryTerm>{` for the energy produced. These fees are part of the rent to BLM.`}</p>
            </ProcessCard>
            <ProcessCard step={5} name={'Decommission and reclaim'} mdxType="ProcessCard">
              <p>{`At the close of a solar or wind project, developers must remove all facilities and return the land to a sound environmental state. Under the proposed rule, when an applicant gains a solar or wind energy lease or grant, they must provide BLM a bond. BLM holds the bond through the life of the project. The bond ensures compliance with terms and conditions, including site `}<GlossaryTerm mdxType="GlossaryTerm">{`reclamation`}</GlossaryTerm>{`.`}</p>
              <br />
BLM returns the bond at the close of the project if the developer appropriately decommissions solar and wind projects and meets terms and conditions.
            </ProcessCard>
            <ProcessCard name={'Rates and fees'} defaultExpanded={true} mdxType="ProcessCard">
              <ul>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "/sites/whats-new-v7.10.0/how-revenue-works/revenues/#Solar-and-wind-energy"
                  }}>{`Renewables rates and fees`}</a></li>
              </ul>
            </ProcessCard>
            <ProcessCard name={'Learn more'} defaultExpanded={true} mdxType="ProcessCard">
              <ul>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "https://www.blm.gov/programs/energy-and-minerals/renewable-energy/solar-energy"
                  }}>{`Solar Energy `}<OpenInNewIcon style={{
                      fontSize: 20
                    }} mdxType="OpenInNewIcon" /></a></li>
                <li parentName="ul"><a parentName="li" {...{
                    "href": "https://www.blm.gov/sites/blm.gov/files/AboutUs_LawsandRegs_FLPMA.pdf"
                  }}>{`The Federal Land Policy and Management Act of 1976, as Amended (PDF) `}<OpenInNewIcon style={{
                      fontSize: 20
                    }} mdxType="OpenInNewIcon" /></a></li>
              </ul>
            </ProcessCard>
          </Box>
        </Grid>
      </Grid>
    </OnshoreRenewablesBackground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      